import { useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react'
import type { Product } from '../../types'
import { CARD_MIN_WIDTH, CARD_GAP, CARD_HEIGHT, OVERSCAN } from '../../lib/catalogConfig'
import ProductCard, { CardWrapper } from './ProductCard'

export interface VirtualGridHandle {
  scrollToTop: () => void
}

const VirtualGrid = forwardRef<VirtualGridHandle, {
  items: Product[]
  onClickItem: (id: string) => void
  isFavorite: (id: string) => boolean
  onFavoriteToggle: (id: string, e: React.MouseEvent) => void
  onScrollChange?: (scrolled: boolean) => void
}>(({ items, onClickItem, isFavorite, onFavoriteToggle, onScrollChange }, ref) => {
  const containerRef              = useRef<HTMLDivElement>(null)
  const [cols, setCols]           = useState(4)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewHeight, setViewHeight] = useState(600)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width
      setCols(Math.max(1, Math.floor((w + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))))
      setViewHeight(window.innerHeight - el.getBoundingClientRect().top - 8)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useImperativeHandle(ref, () => ({
    scrollToTop: () => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }), [])

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop
    setScrollTop(top)
    onScrollChange?.(top > 300)
  }, [onScrollChange])

  const ROW_H    = CARD_HEIGHT + CARD_GAP
  const rowCount = Math.ceil(items.length / cols)
  const totalH   = rowCount * ROW_H
  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const lastRow  = Math.min(rowCount - 1, Math.ceil((scrollTop + viewHeight) / ROW_H) + OVERSCAN)

  const visibleRows = useMemo(() => {
    const rows = []
    for (let r = firstRow; r <= lastRow; r++) {
      const rowItems: Product[] = []
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c
        if (idx < items.length) rowItems.push(items[idx])
      }
      rows.push({ rowIndex: r, items: rowItems })
    }
    return rows
  }, [firstRow, lastRow, cols, items])

  return (
    <div
      ref={containerRef}
      style={{ height: viewHeight, overflowY: 'auto', position: 'relative' }}
      onScroll={onScroll}
      className="scrollbar-thin px-4 my-2 lg:my-2 py-2 lg:py-1"
    >
      <div style={{ height: totalH, position: 'relative' }}>
        {visibleRows.map(({ rowIndex, items: rowItems }) => (
          <div
            key={rowIndex}
            style={{
              position: 'absolute',
              top: rowIndex * ROW_H,
              left: 0, right: 0,
              height: CARD_HEIGHT,
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: CARD_GAP,
            }}
          >
            {rowItems.map((product, c) => (
              <CardWrapper key={product.id}>
                <ProductCard
                  product={product}
                  index={rowIndex * cols + c}
                  onClick={() => onClickItem(product.id)}
                  isFavorite={isFavorite(product.id)}
                  onFavoriteToggle={e => { e.stopPropagation(); onFavoriteToggle(product.id, e) }}
                />
              </CardWrapper>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
})
VirtualGrid.displayName = 'VirtualGrid'

export default VirtualGrid